import { HttpErrorResponse } from '@angular/common/http';

const CONTROL_CHARS = /[\u0000-\u001F\u007F]/
const HTML_OR_SCRIPT = /<[^>]*>|javascript:|data:text\/html|on\w+\s*=/i;
/** Angular/HttpClient transport strings that leak request URLs. */
const RAW_HTTP_NOISE =
  /https?:\/\/|localhost:\d+|\/api\/|Http failure response|Internal Server Error/i;
/** Backend 503 SF-unavailable copy is intentionally long (~450 chars). */
const API_DETAIL_MAX = 800;

/** Typed API failure produced by the global error interceptor. */
export class AppHttpError extends Error {
  readonly status: number;
  readonly original?: HttpErrorResponse;
  /** Shape compatible with existing page handlers that read `err.error?.detail`. */
  readonly error: { detail: string };

  constructor(message: string, status: number, original?: HttpErrorResponse) {
    super(message);
    this.name = 'AppHttpError';
    this.status = status;
    this.original = original;
    this.error = { detail: message };
  }
}

/** Strip markup/control chars from API `detail` without truncating ops messages. */
export function sanitizeApiDetail(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') {
    return fallback;
  }

  const text = raw
    .replace(/<[^>]*>/g, '')
    .replace(CONTROL_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text || text.length > API_DETAIL_MAX || HTML_OR_SCRIPT.test(text) || RAW_HTTP_NOISE.test(text)) {
    return fallback;
  }

  return text;
}

/** Prefer interceptor message, then API `detail`, then fallback — never raw HttpClient URL text. */
export function extractUserFacingError(err: unknown, fallback: string): string {
  if (err instanceof AppHttpError && err.message) {
    return sanitizeApiDetail(err.message, fallback);
  }
  if (err instanceof HttpErrorResponse) {
    return sanitizeApiDetail(err.error?.detail, fallback);
  }
  const nested = (err as { error?: { detail?: unknown } })?.error?.detail;
  if (typeof nested === 'string') {
    return sanitizeApiDetail(nested, fallback);
  }
  if (err instanceof Error && err.message) {
    return sanitizeApiDetail(err.message, fallback);
  }
  return fallback;
}

/** True when the shell banner already owns this failure (do not duplicate inline). */
export function isShellHandledHttpError(err: unknown): boolean {
  if (err instanceof AppHttpError) {
    return err.status === 0 || err.status >= 500;
  }
  if (err instanceof HttpErrorResponse) {
    return err.status === 0 || err.status >= 500;
  }
  return false;
}
